// One shared-world WebSocket (docs/MULTIPLAYER.md): hello on every connect, gentle reconnects,
// numbered block ops, throttled positions and keep-alives. Browser globals are only read when used.
export const FATAL_CODES = new Set([4001, 4002, 4003, 4004, 4005, 4006]);
export const BACKOFF = [1, 2, 4, 8, 16, 30]; // seconds, plus up to 25% jitter
export const PING_MS = 25000;
export const HB_MS = 4 * 60 * 1000; // {t:'hb'} keeps a quiet world in "Friends playing now"
export const POS_MS = 125; // 8 Hz
export const MAX_OPS = 6000;
const OPEN = 1;
const OP_FIELDS = { place: ['x', 'y', 'z', 'id'], remove: ['x', 'y', 'z', 'id'], mine: ['x', 'y', 'z', 'id'], harvest: ['x', 'y', 'z', 'id', 'secs'], device: ['x', 'y', 'z', 'on'] };
const ANY_FIELDS = ['x', 'y', 'z', 'id', 'secs', 'on'];
const r1 = (v) => Math.round(v * 10) / 10;
const r2 = (v) => Math.round(v * 100) / 100;
const num = (v) => (Number.isFinite(v) ? v : 0);
const isObj = (v) => !!v && typeof v === 'object';

export function roomUrl(code, loc = globalThis.location) {
  return `${loc?.protocol === 'https:' ? 'wss' : 'ws'}://${loc?.host || 'localhost'}/api/room/${encodeURIComponent(code)}/ws`;
}

export function posFrame(p) {
  return { t: 'p', x: r2(p.x), y: r2(p.y), z: r2(p.z), yw: r2(num(p.yw)), vy: r1(num(p.vy)), g: p.g ? 1 : 0, sw: r2(num(p.sw)), sp: r1(num(p.sp)), r: p.r ? 1 : 0 };
}
const samePos = (a, b) => ['x', 'y', 'z', 'yw', 'vy', 'g', 'sw', 'sp', 'r'].every((k) => a[k] === b[k]);

function opFrame(op, s) {
  const out = { s, k: op.k };
  for (const f of OP_FIELDS[op.k] || ANY_FIELDS) if (op[f] !== undefined) out[f] = f === 'on' ? (op.on ? 1 : 0) : op[f];
  return out;
}

function failure(code, reason, fatal) {
  return Object.assign(new Error(reason || 'closed'), { code, reason, fatal });
}

export class RoomClient {
  constructor({ WebSocketImpl, url, timers, now, random } = {}) {
    const g = globalThis;
    this.WebSocketImpl = WebSocketImpl;
    this.url = url || ((code) => roomUrl(code));
    this.timers = timers || {
      setTimeout: (f, ms) => g.setTimeout(f, ms),
      clearTimeout: (id) => g.clearTimeout(id),
      setInterval: (f, ms) => g.setInterval(f, ms),
      clearInterval: (id) => g.clearInterval(id),
    };
    this.now = now || (() => Date.now());
    this.random = random || Math.random;
    this.status = 'idle';
    this.handlers = new Map();
    this.ws = null;
    this.ready = false; // a snapshot arrived on this socket
    this.code = null;
    this.hello = null;
    this.snapshot = null;
    this.retry = true;
    this.timeout = 8000;
    this.seq = 0;
    this.failures = 0;
    this.waiter = null;
    this.connectTimer = this.retryTimer = this.pingTimer = this.hbTimer = null;
    this.lastPos = this.nextPos = null;
    this.posAt = -Infinity;
    this.forcePos = false;
  }

  on(type, fn) {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type).add(fn);
    return () => this.handlers.get(type)?.delete(fn);
  }

  emit(type, ...args) {
    for (const fn of [...(this.handlers.get(type) || [])])
      try {
        fn(...args);
      } catch (e) {
        globalThis.console?.error(e);
      }
  }

  setStatus(s) {
    if (this.status === s) return;
    this.status = s;
    this.emit('status', s);
  }

  open(code, hello, { retry = true, timeout = 8000 } = {}) {
    this.stop();
    this.settle(failure(1000, 'closed', false));
    Object.assign(this, { code, retry, timeout, failures: 0, snapshot: null, lastPos: null, nextPos: null, forcePos: false });
    this.hello = { ...hello, t: 'hello', v: 1 };
    const first = new Promise((resolve, reject) => (this.waiter = { resolve, reject }));
    this.setStatus('connecting');
    this.connect();
    return first;
  }

  close() {
    this.stop();
    this.setStatus('closed');
    this.settle(failure(1000, 'closed', false));
  }

  send(obj) {
    if (!this.ws || (!this.ready && obj?.t !== 'hello')) return false;
    return this.raw(JSON.stringify(obj));
  }

  // One frame; returns the sequence numbers given to ops (in order), or [] when nothing was sent.
  sendOps(ops) {
    if (!this.ready || !Array.isArray(ops) || !ops.length) return [];
    const list = ops.slice(0, MAX_OPS).map((op) => opFrame(op, ++this.seq));
    return this.send({ t: 'ops', ops: list }) ? list.map((o) => o.s) : [];
  }

  // Call every frame: sends at most 8 times a second, only when something changed (or `force`, e.g. after a join).
  sendPos(p, force = false) {
    if (p) {
      if (![p.x, p.y, p.z].every(Number.isFinite)) return false;
      this.nextPos = posFrame(p);
      if (this.hello) this.hello.pos = [this.nextPos.x, this.nextPos.y, this.nextPos.z, this.nextPos.yw];
    }
    if (force) this.forcePos = true;
    const f = this.nextPos, now = this.now();
    if (!f || !this.ready || now - this.posAt < POS_MS) return false;
    if (!this.forcePos && this.lastPos && samePos(f, this.lastPos)) return false;
    if (!this.send(f)) return false;
    this.lastPos = f;
    this.posAt = now;
    this.forcePos = false;
    return true;
  }

  // ---------- internals ----------
  connect() {
    this.retryTimer = null;
    let ws;
    try {
      ws = new (this.WebSocketImpl || globalThis.WebSocket)(this.url(this.code));
    } catch (e) {
      this.emit('closed', { code: 0, reason: 'error', fatal: false });
      return this.failed(0, 'error');
    }
    this.ws = ws;
    this.ready = false;
    ws.onopen = () => this.ws === ws && this.raw(JSON.stringify(this.hello));
    ws.onmessage = (e) => this.ws === ws && this.receive(e?.data);
    ws.onerror = () => {}; // a close event always follows
    ws.onclose = (e) => this.ws === ws && this.dropped(Number(e?.code) || 1006, e?.reason || '');
    this.connectTimer = this.timers.setTimeout(() => {
      this.connectTimer = null;
      if (this.ws !== ws || this.ready) return;
      this.stop();
      this.emit('closed', { code: 0, reason: 'timeout', fatal: false });
      this.failed(0, 'timeout');
    }, this.timeout);
  }

  receive(data) {
    if (typeof data !== 'string' || data === 'pong') return;
    let msg;
    try {
      msg = JSON.parse(data);
    } catch (e) {
      return;
    }
    if (!isObj(msg) || typeof msg.t !== 'string') return;
    if (msg.t === 'snapshot') {
      if (!isObj(msg.you) || !isObj(msg.room) || !isObj(msg.world)) return;
      this.ready = true;
      this.failures = 0;
      this.snapshot = msg;
      this.lastPos = null;
      if (typeof msg.you.token === 'string' && msg.you.token) this.hello.token = msg.you.token;
      if (this.connectTimer !== null) this.timers.clearTimeout(this.connectTimer);
      this.connectTimer = null;
      this.pingTimer ??= this.timers.setInterval(() => this.raw('ping'), PING_MS);
      this.hbTimer ??= this.timers.setInterval(() => this.send({ t: 'hb' }), HB_MS);
      this.setStatus('online');
      const w = this.waiter;
      this.waiter = null;
      w?.resolve(msg);
    }
    this.emit(msg.t, msg);
  }

  dropped(code, reason) {
    this.detach();
    const fatal = FATAL_CODES.has(code);
    this.emit('closed', { code, reason, fatal });
    if (!fatal) return this.failed(code, reason || 'closed');
    this.setStatus('closed');
    this.settle(failure(code, reason, true));
  }

  failed(code, reason) {
    if (!this.retry) {
      this.setStatus('closed');
      return this.settle(failure(code, reason, false));
    }
    this.setStatus('offline');
    const base = BACKOFF[Math.min(this.failures++, BACKOFF.length - 1)];
    this.retryTimer = this.timers.setTimeout(() => this.connect(), Math.round(base * 1000 * (1 + 0.25 * this.random())));
  }

  raw(data) {
    try {
      if (this.ws?.readyState !== OPEN) return false;
      this.ws.send(data);
      return true;
    } catch (e) {
      return false;
    }
  }

  detach() {
    const ws = this.ws;
    this.ws = null;
    this.ready = false;
    if (this.connectTimer !== null) this.timers.clearTimeout(this.connectTimer);
    if (this.pingTimer !== null) this.timers.clearInterval(this.pingTimer);
    if (this.hbTimer !== null) this.timers.clearInterval(this.hbTimer);
    this.connectTimer = this.pingTimer = this.hbTimer = null;
    if (ws) ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
  }

  // Forget the current socket (closing it) and any pending reconnect.
  stop() {
    const ws = this.ws;
    this.detach();
    if (this.retryTimer !== null) this.timers.clearTimeout(this.retryTimer);
    this.retryTimer = null;
    try {
      ws?.close(1000);
    } catch (e) {
      /* already closing */
    }
  }

  settle(error) {
    const w = this.waiter;
    this.waiter = null;
    w?.reject(error);
  }
}
